/**
 * DateSeparator - Separador visual de data entre mensagens
 * Exibe o dia da semana e a data formatada
 */

import React from 'react';

interface DateSeparatorProps {
  /** Data a ser exibida */
  date: Date;
}

/**
 * Formata a data para exibição no separador
 * Exemplo: "Sábado (21/12/2024)"
 */
const formatDateSeparator = (date: Date): string => {
  const daysOfWeek = [
    'Domingo',
    'Segunda-feira',
    'Terça-feira',
    'Quarta-feira',
    'Quinta-feira',
    'Sexta-feira',
    'Sábado'
  ];

  const dayOfWeek = daysOfWeek[date.getDay()];
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  // Verificar se é hoje
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  // Verificar se é ontem
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    return 'Hoje';
  }

  if (isYesterday) {
    return 'Ontem';
  }

  return `${dayOfWeek} (${day}/${month}/${year})`;
};

/**
 * Componente de separador de data para o chat
 * Aparece centralizado entre mensagens quando a data muda
 *
 * @example
 * <DateSeparator date={new Date('2024-12-21')} />
 */
const DateSeparator: React.FC<DateSeparatorProps> = ({ date }) => {
  const formattedDate = formatDateSeparator(date);

  return (
    <div className="flex items-center justify-center my-4">
      <div
        className="px-4 py-1.5 rounded-full text-xs font-medium shadow-sm"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          color: 'var(--theme-muted-text)',
          border: '1px solid var(--theme-card-border)'
        }}
      >
        {formattedDate}
      </div>
    </div>
  );
};

export default DateSeparator;

/**
 * Verifica se duas datas são do mesmo dia
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Extrai a data de uma mensagem (sentAt ou createdAt)
 */
export const getMessageDate = (message: { sentAt?: string | Date; createdAt?: string | Date }): Date => {
  const dateStr = message.sentAt || message.createdAt;
  return dateStr ? new Date(dateStr) : new Date();
};
